import { z } from 'zod';

// ─── pharmacy order vocabularies ────────────────────────────────────────────
//
// Governed enums for prescriber-initiated pharmacy orders (the affiliate order
// that tells GoGoMeds or HealthWarehouse who pays). The `as const` arrays drive
// the `text`+CHECK constraints in the DB schema, the wire schemas, and the
// value-qualified metric tags — a native `pgEnum` is banned by ADR-056 Rule 8
// and ADR-105 Rule 2. Row shapes are not mirrored here: readers consume
// `typeof memberPharmacyOrders.$inferSelect` (ADR-066).
//
// An order's CURRENT status is not a column. It is derived from the newest
// `member_pharmacy_order_status_events` row by `currentPharmacyOrderStatus`
// in `packages/shared/src/db/pharmacy-orders.ts`, the single definition.

/** The pharmacy an order is routed to, by the prescription's `pharmacy_id`. */
export const PHARMACY_VENDOR = ['gogomeds', 'healthwarehouse'] as const;
export const pharmacyVendorSchema = z.enum(PHARMACY_VENDOR);
export type PharmacyVendor = z.infer<typeof pharmacyVendorSchema>;

/**
 * `pending`: written before any vendor call; the only claimable state.
 * `submitted`: the vendor accepted it. `held`: a required input was missing,
 * so no vendor call was made. `rejected`: the vendor refused it on validation
 * and it is not retried. `failed`: transport failures exhausted the retry
 * budget. `cancelled`: the prescription was cancelled before a successful
 * submission. `handled`: ops submitted it through the vendor's own route, or
 * decided none was needed.
 *
 * There is deliberately no `approved` — nobody approves a prescriber's order —
 * and no `shipped`/`delivered`, which nothing here observes.
 */
export const PHARMACY_ORDER_STATUS = [
  'pending',
  'submitted',
  'held',
  'rejected',
  'failed',
  'cancelled',
  'handled',
] as const;
export const pharmacyOrderStatusSchema = z.enum(PHARMACY_ORDER_STATUS);
export type PharmacyOrderStatus = z.infer<typeof pharmacyOrderStatusSchema>;

/** The three statuses ops are told about and may mark handled. */
export const PHARMACY_ORDER_ACTIONABLE_STATUS = ['held', 'rejected', 'failed'] as const;
export const pharmacyOrderActionableStatusSchema = z.enum(PHARMACY_ORDER_ACTIONABLE_STATUS);
export type PharmacyOrderActionableStatus = z.infer<typeof pharmacyOrderActionableStatusSchema>;

/**
 * Why an order is held, rejected or failed. Specific enough to act on: the
 * input that was missing, or which block the vendor refused. A vendor
 * address refusal names whose address it was, because backend2's error text
 * read as a member-address problem when the prescriber's was at fault.
 *
 * `member_gender` is reserved: gender is sent as `U` when unknown, so nothing
 * holds on it today, but a vendor refusal may map to it.
 *
 * `medical_information_unavailable` is a Healthie read that failed in a way a
 * retry cannot fix (a refused credential or a rejected query), or stayed
 * unavailable through every retry. It is kept apart from `medical_information`
 * so ops look at the integration, not the member's intake form.
 *
 * `medical_conditions` is a medical record with no usable answer on medical
 * conditions: never answered (a 1.0 record), or a yes with no text. Both
 * vendors require the field, and "none" would be a clinical claim the member
 * never made, so the order is held instead.
 *
 * `controlled_identity_unavailable` is the controlled-substance identity (the
 * SSN and licence source, or the government ID document) staying unavailable
 * through every retry. It is kept apart from `transport_exhausted`, which is the
 * pharmacy not answering, so ops look at the right system.
 *
 * `vendor_outcome_unknown` is a HealthWarehouse order that may have been
 * placed: its `POST /orders` was sent but no readable answer was recorded.
 * HealthWarehouse has no order idempotency key, so it is never resent; ops
 * check the HealthWarehouse portal before submitting it by hand.
 */
export const PHARMACY_ORDER_REASON_CODE = [
  'member_name',
  'member_dob',
  'member_gender',
  'member_phone',
  'member_address',
  'medical_information',
  'medical_information_unavailable',
  'medical_conditions',
  'drug_id_unparseable',
  'drug_mapping_absent',
  'ndc_absent',
  'hwh_product_absent',
  'quantity',
  'prescriber_unresolved',
  'prescriber_name',
  'prescriber_npi',
  'prescriber_mailing_address',
  'ssn',
  'government_id_document',
  'government_id_format',
  'drivers_licence',
  'controlled_identity_unavailable',
  'vendor_validation_member_address',
  'vendor_validation_prescriber_address',
  'vendor_validation_other',
  'transport_exhausted',
  'vendor_outcome_unknown',
] as const;
export const pharmacyOrderReasonCodeSchema = z.enum(PHARMACY_ORDER_REASON_CODE);
export type PharmacyOrderReasonCode = z.infer<typeof pharmacyOrderReasonCodeSchema>;

/**
 * How ops closed an actionable order. A closed pair rather than free text, so
 * no PHI lands in the status event.
 */
export const PHARMACY_ORDER_RESOLUTION = ['submitted_via_vendor', 'not_required'] as const;
export const pharmacyOrderResolutionSchema = z.enum(PHARMACY_ORDER_RESOLUTION);
export type PharmacyOrderResolution = z.infer<typeof pharmacyOrderResolutionSchema>;

/**
 * Who caused a status event. `system` for every pipeline transition;
 * `internal_user` only for `handled`, carrying that staff member's id. A
 * member never acts on a pharmacy order.
 */
export const PHARMACY_ORDER_ACTOR_KIND = ['system', 'internal_user'] as const;
export const pharmacyOrderActorKindSchema = z.enum(PHARMACY_ORDER_ACTOR_KIND);
export type PharmacyOrderActorKind = z.infer<typeof pharmacyOrderActorKindSchema>;

/**
 * The last transport failure an order hit while it stayed `pending`. All of
 * these retry; a validation refusal never lands here. `auth` covers a 401 or
 * 403, retried because credentials may be mid-rotation.
 */
export const PHARMACY_ORDER_ERROR_CLASS = [
  'network',
  'timeout',
  'server_error',
  'rate_limited',
  'auth',
  'unreadable_response',
] as const;
export const pharmacyOrderErrorClassSchema = z.enum(PHARMACY_ORDER_ERROR_CLASS);
export type PharmacyOrderErrorClass = z.infer<typeof pharmacyOrderErrorClassSchema>;

/** Values of the `pharmacy_order_outcome` metric tag. */
export const PHARMACY_ORDER_OUTCOME = [
  'not_ours',
  'not_owned',
  'pre_ownership',
  'cancelled_at_creation',
  'unconfigured',
  ...PHARMACY_ORDER_STATUS,
] as const;
export type PharmacyOrderOutcome = (typeof PHARMACY_ORDER_OUTCOME)[number];

/** Values of the `pharmacy_vendor_step` metric tag. */
export const PHARMACY_VENDOR_STEP = ['token', 'patient', 'customer', 'order', 'submit'] as const;
export type PharmacyVendorStep = (typeof PHARMACY_VENDOR_STEP)[number];

/**
 * Values of the `pharmacy_order_age` tag on the actionable-backlog gauge. Each
 * bucket is cumulative, the count of orders waiting at least that long, so a
 * monitor reads one series with `max` and never sums across buckets.
 */
export const PHARMACY_ORDER_BACKLOG_AGE = ['any', 'over_1h', 'over_4h', 'over_24h'] as const;
export type PharmacyOrderBacklogAge = (typeof PHARMACY_ORDER_BACKLOG_AGE)[number];

/** Upper bound on a stored vendor field path (a path, never a value). */
export const PHARMACY_ORDER_FIELD_PATH_MAX_LENGTH = 512;

// ─── events ─────────────────────────────────────────────────────────────────

/**
 * `rx.pharmacy_order.attention_required` payload. Pointer-only by
 * construction: an opaque order id, closed vocabularies and a timestamp. The
 * member rides on the envelope's `actor.memberId`, used only for the Atlas
 * link. Strict, because it is a producer-side contract.
 */
export const rxPharmacyOrderAttentionPayloadSchema = z
  .object({
    orderId: z.string().uuid(),
    vendor: pharmacyVendorSchema,
    outcome: pharmacyOrderActionableStatusSchema,
    reasonCodes: z.array(pharmacyOrderReasonCodeSchema).min(1),
    occurredAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type RxPharmacyOrderAttentionPayload = z.infer<typeof rxPharmacyOrderAttentionPayloadSchema>;

// ─── staff wire contract ────────────────────────────────────────────────────

/** Fields on the manual-submission sheet ops copy into the vendor's route. */
export const PHARMACY_ORDER_SHEET_FIELD = [
  'first_name',
  'last_name',
  'birth_date',
  'gender',
  'phone',
  'shipping_address',
  'allergies',
  'current_medications',
  'medical_conditions',
  'pregnancy',
  'drivers_licence_number',
  'prescription_number',
  'drug_name',
  'ndc',
  'quantity',
  'days_supply',
  'sig',
  'prescriber_name',
  'prescriber_npi',
  'prescriber_mailing_address',
] as const;
export const pharmacyOrderSheetFieldSchema = z.enum(PHARMACY_ORDER_SHEET_FIELD);
export type PharmacyOrderSheetField = z.infer<typeof pharmacyOrderSheetFieldSchema>;

export const PHARMACY_ORDER_SHEET_GROUP = ['member', 'prescription', 'provider'] as const;
export const pharmacyOrderSheetGroupSchema = z.enum(PHARMACY_ORDER_SHEET_GROUP);
export type PharmacyOrderSheetGroup = z.infer<typeof pharmacyOrderSheetGroupSchema>;

// Response schemas are client-parsed, so none is `.strict()` (ADR-135 Rule 1).

/** A sheet value, or `null` when that input is missing. */
export const pharmacyOrderSheetEntrySchema = z.object({
  field: pharmacyOrderSheetFieldSchema,
  value: z.string().nullable(),
});
export type PharmacyOrderSheetEntry = z.infer<typeof pharmacyOrderSheetEntrySchema>;

export const pharmacyOrderSheetSchema = z.object({
  groups: z.array(
    z.object({
      group: pharmacyOrderSheetGroupSchema,
      entries: z.array(pharmacyOrderSheetEntrySchema),
    }),
  ),
});
export type PharmacyOrderSheet = z.infer<typeof pharmacyOrderSheetSchema>;

export const staffPharmacyOrderSchema = z.object({
  id: z.string().uuid(),
  vendor: pharmacyVendorSchema,
  status: pharmacyOrderStatusSchema,
  reasonCodes: z.array(pharmacyOrderReasonCodeSchema),
  vendorFieldPath: z.string().nullable(),
  resolution: pharmacyOrderResolutionSchema.nullable(),
  createdAt: z.string().datetime({ offset: true }),
  statusChangedAt: z.string().datetime({ offset: true }),
  /** Present only for `held`, `rejected` and `failed` orders. */
  sheet: pharmacyOrderSheetSchema.nullable(),
});
export type StaffPharmacyOrder = z.infer<typeof staffPharmacyOrderSchema>;

export const staffPharmacyOrdersResponseSchema = z.object({
  orders: z.array(staffPharmacyOrderSchema),
});
export type StaffPharmacyOrdersResponse = z.infer<typeof staffPharmacyOrdersResponseSchema>;

/** Mark-handled request. Strict: the server rejects anything but the resolution. */
export const pharmacyOrderHandledRequestSchema = z
  .object({ resolution: pharmacyOrderResolutionSchema })
  .strict();
export type PharmacyOrderHandledRequest = z.infer<typeof pharmacyOrderHandledRequestSchema>;

/** Mark-handled response: the order as it now stands (a handled order carries no sheet). */
export const staffPharmacyOrderHandledResponseSchema = z.object({
  order: staffPharmacyOrderSchema,
});
export type StaffPharmacyOrderHandledResponse = z.infer<
  typeof staffPharmacyOrderHandledResponseSchema
>;
